import { AnswerStreamEvent } from './types';
import type { AnswerStreamSubject } from './context';

export type StreamStatus =
  | 'idle'
  | 'submitting'
  | 'awaiting'
  | 'streaming'
  | 'complete'
  | 'error';

export type StreamState = {
  status: StreamStatus;
  streamId: string | null;
  output: string | null;
  review: string | null;
  isCorrect: boolean | null;
  error: string | null;
};

export function createInitialStreamState({
  output,
  review,
  isCorrect,
}: {
  output: string | null;
  review: string | null;
  isCorrect: boolean | null;
}): StreamState {
  return {
    status: 'idle',
    streamId: null,
    output,
    review,
    isCorrect,
    error: null,
  };
}

export function matchesAnswerStreamTarget(
  event: AnswerStreamEvent,
  subject: AnswerStreamSubject,
  questionSlug: string
): boolean {
  return (
    event.subjectType === subject.subjectType &&
    event.subjectId === subject.subjectId &&
    event.questionSlug === questionSlug
  );
}

export function reduceAnswerStreamEvent(
  current: StreamState,
  event: AnswerStreamEvent
): StreamState {
  if (event.type === 'answer-stream-start') {
    if (current.status === 'streaming') {
      return current;
    }
    return {
      status: 'streaming',
      streamId: event.streamId,
      output: '',
      review: '',
      isCorrect: null,
      error: null,
    };
  }

  if (event.type === 'answer-stream-state') {
    if (
      current.status === 'streaming' &&
      (event.streamId !== current.streamId ||
        ((event.status === 'pending' || event.status === 'streaming') &&
          ((current.output?.length ?? 0) > 0 ||
            (current.review?.length ?? 0) > 0)))
    ) {
      return current;
    }
    return {
      status:
        event.status === 'done'
          ? 'complete'
          : event.status === 'error'
          ? 'error'
          : 'streaming',
      streamId: event.streamId,
      output: event.output,
      review: event.review,
      isCorrect: event.isCorrect,
      error: event.message,
    };
  }

  if (!current.streamId || event.streamId !== current.streamId) {
    return current;
  }

  if (event.type === 'answer-stream-chunk') {
    return {
      ...current,
      status: 'streaming',
      [event.field]: `${current[event.field] ?? ''}${event.value}`,
    };
  }

  if (event.type === 'answer-stream-end') {
    return {
      status: 'complete',
      streamId: event.streamId,
      output: event.output,
      review: event.review,
      isCorrect: event.isCorrect,
      error: null,
    };
  }

  return {
    ...current,
    status: 'error',
    error: event.message,
  };
}
